import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Calendar, XCircle, CheckCircle2, CreditCard, UserPlus, ShieldCheck, AlertTriangle } from "lucide-react";

const TYPE_CONFIG = {
  booking_approved: { icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/10" },
  booking_cancelled: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/10" },
  new_booking: { icon: Calendar, color: "text-teal-400", bg: "bg-teal-500/10" },
  payment_confirmed: { icon: CreditCard, color: "text-sky-400", bg: "bg-sky-500/10" },
  payment_verified: { icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/10" },
  new_customer: { icon: UserPlus, color: "text-amber-400", bg: "bg-amber-500/10" },
  ai_verified: { icon: ShieldCheck, color: "text-emerald-400", bg: "bg-emerald-500/10" },
  ai_warning: { icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10" },
};

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return "Vừa xong";
  if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
  return `${Math.floor(diff / 86400)} ngày trước`;
}

// Create notification sound using Web Audio API
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // Two-tone chime
    const playTone = (freq, startTime, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = "sine";
      gain.gain.setValueAtTime(0.3, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };
    const now = ctx.currentTime;
    playTone(880, now, 0.15);        // A5
    playTone(1174.66, now + 0.15, 0.2); // D6
  } catch (e) {
    // Audio not supported or blocked
  }
}

// Request Chrome push notification permission
function requestNotificationPermission() {
  if ("Notification" in window && Notification.permission === "default") {
    // This works on mount in most browsers
    Notification.requestPermission().then(perm => {
    });
  }
}

// Show Chrome desktop notification
function showDesktopNotification(title, message) {
  if ("Notification" in window && Notification.permission === "granted") {
    try {
      const notif = new Notification(title, {
        body: message,
        icon: "/favicon.ico",
        badge: "/favicon.ico",
        tag: "karinox-" + Date.now(),
        requireInteraction: false,
      });
      setTimeout(() => notif.close(), 8000);
    } catch (e) {
      // Fallback
    }
  }
}

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const prevUnreadRef = useRef(0);
  const isFirstLoad = useRef(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [notifPermission, setNotifPermission] = useState(
    "Notification" in window ? Notification.permission : "denied"
  );

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
    // Update state after request
    if ("Notification" in window) {
      setNotifPermission(Notification.permission);
    }
  }, []);

  const handleEnableNotifications = () => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then(perm => {
        setNotifPermission(perm);
      });
    }
  };

  const handleNotifClick = (notif) => {
    setOpen(false);
    // If notification has booking data, navigate to that specific date & booking
    const bookingDate = notif.booking_date;
    const bookingId = notif.booking_id;
    if (bookingDate && bookingId) {
      const params = new URLSearchParams({ date: bookingDate, booking_id: bookingId });
      navigate(`/?${params.toString()}`);
    } else if (bookingDate) {
      navigate(`/?date=${bookingDate}`);
    } else {
      // Fallback: just go to schedule page
      if (location.pathname !== "/") {
        navigate("/");
      }
    }
  };

  const loadNotifs = useCallback(async () => {
    try {
      const { data } = await api.get("/app-notifications");
      const newNotifs = data.notifications || [];
      const newUnread = data.unread_count || 0;

      setNotifications(newNotifs);
      setUnread(newUnread);

      // Play sound + show Chrome notification when NEW unread arrives (not on first load)
      if (!isFirstLoad.current && newUnread > prevUnreadRef.current && newUnread > 0) {
        playNotificationSound();

        // Find the newest unread notification for Chrome push
        const newestUnread = newNotifs.find(n => !n.read);
        if (newestUnread) {
          showDesktopNotification(
            newestUnread.title || "KARINOX Arena",
            newestUnread.message || "Bạn có thông báo mới"
          );
        }
      }

      prevUnreadRef.current = newUnread;
      isFirstLoad.current = false;
    } catch { }
  }, []);

  useEffect(() => {
    loadNotifs();
    const interval = setInterval(loadNotifs, 15000); // Poll every 15s (faster for real-time feel)
    return () => clearInterval(interval);
  }, [loadNotifs]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleOpen = () => {
    setOpen(!open);
    if (!open && unread > 0) {
      api.post("/app-notifications/read-all").then(() => {
        setUnread(0);
        prevUnreadRef.current = 0;
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      }).catch(() => {});
    }
  };

  return (
    <div className="relative" ref={ref} style={{ zIndex: 9999 }} data-testid="notification-bell">
      <button onClick={handleOpen}
        className="relative p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-[#334155]/50 transition-colors"
        data-testid="bell-button">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-bounce" data-testid="bell-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-[#1e293b] border border-[#334155] rounded-xl shadow-2xl shadow-black/40 overflow-hidden" style={{ zIndex: 9999 }} data-testid="notification-dropdown">
          <div className="px-4 py-3 border-b border-[#334155] flex items-center justify-between">
            <span className="text-sm font-semibold text-[#F8FAFC]">Thông báo</span>
            {notifications.length > 0 && (
              <span className="text-[10px] text-slate-500">{notifications.length} tin</span>
            )}
          </div>
          {/* Permission prompt */}
          {notifPermission === "default" && (
            <button onClick={handleEnableNotifications}
              className="w-full px-4 py-2.5 bg-teal-500/10 border-b border-[#334155] text-xs text-teal-400 hover:bg-teal-500/20 transition-colors flex items-center gap-2"
              data-testid="enable-notifications-btn">
              <Bell className="w-3.5 h-3.5" />
              <span>Bật thông báo Chrome để nhận tin ngay</span>
            </button>
          )}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                <p className="text-xs text-slate-500">Chưa có thông báo</p>
              </div>
            ) : (
              notifications.map(n => {
                const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.new_booking;
                const Icon = cfg.icon;
                return (
                  <div key={n.id} onClick={() => handleNotifClick(n)}
                    className={`px-4 py-3 border-b border-[#334155]/50 hover:bg-[#334155]/20 transition-colors cursor-pointer ${!n.read ? "bg-[#334155]/10" : ""}`}>
                    <div className="flex gap-3">
                      <div className={`w-8 h-8 rounded-full ${cfg.bg} flex items-center justify-center shrink-0`}>
                        <Icon className={`w-4 h-4 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-slate-200">{n.title}</span>
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" />}
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">{n.message}</p>
                        <span className="text-[10px] text-slate-600 mt-1 block">{timeAgo(n.created_at)}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
